import { createContext } from '@lit/context';
import { EnrollmentComponent, Module, PreloadResource, PreloadSettings } from '../../shared/types/common.model';
import { Skill } from '../../shared/types/skills.model';
import { CompletionItem } from './completion-item';
import { SocialMedia } from './course-player.model';
import { LearningJourneyContext, LearningJourneyState } from '../../shared/types/learning-journeys.model';

export interface CoursePlayerState {
  currentRoute: string;
  /**
   * Indicates if the enrollment status is one of the following: PASSED, COMPLETED, or FAILED
   * on initial load, or if the backend has returned a new status (e.g., course just completed - wasJustCompleted flag).
   */
  isCompleted: boolean;
  /**
   * Indicates if the completion view is active. This is true when the enrollment is completed and the user is on the enrollment page
   * (e.g., /enrollments/1307110) rather than a module page (e.g., /enrollments/1307110/page/2127021).
   */
  isCompletionViewActive: boolean;
  enrollment: PreloadResource['enrollment']['attributes'];
  courseStructure: Module[];
  isSidenavOpen: boolean;
  /** Flag to disable sidenav when embedded */
  isSidenavDisabled: boolean;
  currentModule: EnrollmentComponent | null;
  isLoading: boolean;
  onDescriptionPage: PreloadResource['on_description_page'];
  /**
   * Flag to show if admin is previewing course
   */
  isPreview: PreloadResource['is_preview'];
  flags: Partial<PreloadResource['flags']['attributes']>;
  settings: Partial<PreloadSettings>;
  nextCourseOnPath?: number | null;
  /**
   * Flag to show activate exam mode (no sidenav)
   */
  isTakingExam?: boolean;
  /**
   * Flag to show exit exam button
   */
  showExitModuleButton?: boolean;
  /**
   * Flag to show exit course button
   * hidden for LUA users
   */
  showExitCourseButton?: boolean;
  completePage: {
    items: CompletionItem[];
    isShareModalOpen?: boolean;
    social_sharing?: {
      linkedin_is_enabled?: boolean;
      social_sharing_enabled?: number;
      twitter_hash_tags?: string;
      twitter_is_enabled?: boolean;
      twitter_related_accounts?: string;
      twitter_url?: string;
    };
    awarded_badges?: PreloadResource['awarded_badges']['data'];
    awarded_skills?: Skill[];
    learningJourneyState?: LearningJourneyState;
  };

  learningJourneyContext?: LearningJourneyContext;

  /** Portal and lightweight feature flags */
  featureFlags: {
    content_details_redesign_enabled?: boolean;
    concurrent_and_series_sessions?: boolean;
  };

  /** Scorm settings */
  scorm: {
    /** If isInline is true, then scorm should be rendered in the page content instead of in a modal */
    isInline: boolean;
    /** Scorm content URL for iframe */
    contentUrl: string | null;
    /** Enable scorm file debugger */
    isDebuggerEnabled: boolean;
    /** Skip scorm launch screen an start Scorm/TinCan module */
    autoPlay: boolean;
  };

  canReAttemptByModule: Record<string, boolean | undefined | null>;

  /** When updating the timestamp, the entire application re-renders */
  timestamp?: number;
  openInstructorMessage: boolean;
  toggleSidenav: (value?: boolean) => void;
  exitCourse: () => void;
  navigateBackToCourse: () => void;
  navigateToEditContent: () => void;
  navigateToPreviousModule: () => void;
  navigateToNextModule: () => void;
  navigateToHome: () => void;
  hasNextModule: () => boolean;
  hasPreviousModule: () => boolean;
  navigateToModule: (moduleId: number) => void;
  confirmCompletion: () => void;
  navigateToContentDetails: () => void;
  showDescriptionPage: () => void;
  navigateToCompletePage: () => void;
  canShowCompletionBar: () => boolean;
  getCompletionItems?: () => CompletionItem[];
  closeSocialLinksModal?: () => void;
  dispatchShareEvent?: (type: SocialMedia) => void;
  navigateToNextCourse: () => void;
  navigateToLearningJourney: () => void;
  leaveModule: () => void;
  launchScorm: (enableDebugger: boolean) => Promise<void>;
  /**
   * Determines if the exit course button should be displayed on the final module page.
   * This should only be visible if the enrollment is completed and the user is on the last module.
   */
  showFooterExitCourseButton: () => boolean;
  navigateToScormContent: (enableDebugger: boolean) => Promise<void>;
  validateScormLaunch: (shouldNotify?: boolean) => Promise<boolean>;
  toggleInstructorMessage: (value: boolean) => void;
}

export const stateContext = createContext<CoursePlayerState>(Symbol('course-player-state'));
